/**
 * Autosave, the site-wide behaviour (Dax, 2026-09-15: no Save buttons). An edit schedules a save that
 * fires once edits pause for AUTOSAVE_DEBOUNCE_MS; only the latest value is sent, one save at a time
 * so an older one can never land after a newer one; a pending edit is flushed when the component goes
 * or the page hides, so leaving inside the debounce loses nothing. The state drives SaveStatus.
 */
import { useEffect, useRef, useState } from 'react';
import { AUTOSAVE_DEBOUNCE_MS } from '@studio/contracts';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface Autosaver<T> {
  schedule: (value: T) => void;
  flush: () => void;
}

/** The engine, free of React so its debounce and ordering can be tested with fake timers. */
export function createAutosaver<T>(
  save: (value: T) => Promise<unknown>,
  report: (state: SaveState, error: string | null) => void,
  delayMs: number = AUTOSAVE_DEBOUNCE_MS,
): Autosaver<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: T } | null = null;
  let busy = false;
  const run = (): void => {
    if (busy || !pending) return;
    const { value } = pending;
    pending = null;
    busy = true;
    save(value).then(() => {
      busy = false;
      if (pending) {
        if (!timer) run();
        return;
      }
      report('saved', null);
    }, (err: unknown) => {
      busy = false;
      report('error', err instanceof Error ? err.message : String(err));
      if (pending && !timer) run();
    });
  };
  return {
    schedule(value: T): void {
      pending = { value };
      report('saving', null);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        run();
      }, delayMs);
    },
    flush(): void {
      if (timer) clearTimeout(timer);
      timer = null;
      run();
    },
  };
}

export interface Autosave<T> {
  schedule: (value: T) => void;
  state: SaveState;
  error: string | null;
}

export function useAutosave<T>(save: (value: T) => Promise<unknown>): Autosave<T> {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });
  const [engine] = useState(() => createAutosaver<T>((value) => saveRef.current(value), (s, e) => {
    setState(s);
    setError(e);
  }));
  useEffect(() => {
    window.addEventListener('pagehide', engine.flush);
    return () => {
      window.removeEventListener('pagehide', engine.flush);
      engine.flush();
    };
  }, [engine]);
  return { schedule: engine.schedule, state, error };
}
